import {
	ReactElement,
	Ref,
	RefObject,
	forwardRef,
	useImperativeHandle,
	useRef,
	useState
} from "react";
import { Events } from "./Events/Events";
import styles from "./CalendarContent.module.less";
import { Header } from "./Header/Header";
import { BusyIndicator, CalendarHighlightDateOnlyRange } from "client/Components";
import {
	CalendarAppointment,
	CalendarDateRange,
	EditAppointmentInfo
} from "../CalendarPage";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { TimeZone } from "client/DateAndTime/timezones";
import { useToday } from "client/Hooks";
import { CalendarContentViewKind } from "client/Api/Calendar/calendar";
import { AppointmentDetailsDialog } from "../AppointmentDetails/AppointmentDetailsDialog";
import { Coordinate } from "client/Misc/commonTypes";
import { TimeOnly } from "client/DateAndTime/timeOnly";
import { MainMenu } from "./MainMenu/MainMenu";
import { AppointmentOrIdentifier } from "../common";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type CalendarContentProps = {
	isBusy: boolean;
	appointments: readonly CalendarAppointment[];
	dateRange: CalendarDateRange;
	isMobile: boolean;
	isResponsive: boolean;
	onNewEvent(): void;
	onDateChange(value: DateOnly): void;
	onViewChange(view: CalendarContentViewKind): void;
	timezone: TimeZone;
	view: CalendarContentViewKind;
	calculateHighlightRangeForDate: (date: DateOnly) => CalendarHighlightDateOnlyRange;
	onCreateAppointment: (
		coordinate: Coordinate,
		date: DateOnly,
		time?: TimeOnly
	) => void;
	onEditAppointment: (
		coordinate: Coordinate,
		data: EditAppointmentInfo
	) => void;
	onAfterMutatingAppointment: (calendarIds: readonly string[]) => void;
	editingAppointmentId: string | undefined;
	onNavigateToOwa?(): void;
};

export type CalendarContentHandler = {
	scrollToMinutes(minutes: number): void;
};

export const CalendarContent = forwardRef(
	(props: CalendarContentProps, ref: Ref<CalendarContentHandler>): ReactElement => {
		const {
			isBusy,
			appointments,
			dateRange,
			isMobile,
			isResponsive,
			onNewEvent,
			onDateChange,
			onViewChange,
			timezone,
			view,
			calculateHighlightRangeForDate,
			onCreateAppointment,
			onEditAppointment,
			onAfterMutatingAppointment,
			editingAppointmentId,
			onNavigateToOwa
		} = props;

		const [appointmentOrIdentifierToShowDetails, setAppointmentOrIdentifierToShowDetails] = useState<AppointmentOrIdentifier | null>(null);

		const eventsRef: RefObject<HTMLDivElement | null> = useRef(null);

		const today: Date = useToday({ timezone });

		useImperativeHandle(
			ref,
			(): CalendarContentHandler => ({
				scrollToMinutes: (minutes: number): void => {
					eventsRef.current?.scrollTo({
						top: minutes
					});
				}
			}),
			[]
		);

		return (
			<>
				<div className={styles.root}>
					{isBusy && <BusyIndicator />}
					<div className={styles.mainMenu}>
						<MainMenu
							onNewEvent={onNewEvent}
							dateRange={dateRange}
							onChange={onDateChange}
							onViewChange={onViewChange}
							isMobile={isMobile}
							isResponsive={isResponsive}
							timezone={timezone}
							view={view}
							calculateHighlightRangeForDate={calculateHighlightRangeForDate}
							onNavigateToOwa={onNavigateToOwa}
						/>
					</div>
					<div className={styles.header}>
						<Header
							appointments={appointments}
							dateRange={dateRange}
							timezone={timezone}
							today={today}
							onOpenDetailsDialog={setAppointmentOrIdentifierToShowDetails}
							onCreate={onCreateAppointment}
							onEdit={onEditAppointment}
							onAfterMutatingAppointment={onAfterMutatingAppointment}
							editingAppointmentId={editingAppointmentId}
						/>
					</div>
					<div
						className={styles.events}
						ref={eventsRef}
					>
						<Events
							appointments={appointments}
							dateRange={dateRange.toRender}
							timezone={timezone}
							today={today}
							onOpenDetailsDialog={setAppointmentOrIdentifierToShowDetails}
							onCreate={onCreateAppointment}
							onEdit={onEditAppointment}
							onAfterMutatingAppointment={onAfterMutatingAppointment}
							editingAppointmentId={editingAppointmentId}
						/>
						<AiAssistantFabSpacing shouldIncludeContentPadding />
					</div>
				</div>
				{appointmentOrIdentifierToShowDetails && (
					<AppointmentDetailsDialog
						appointmentOrIdentifier={appointmentOrIdentifierToShowDetails}
						onClose={() => setAppointmentOrIdentifierToShowDetails(null)}
						onAfterMutatingAppointment={onAfterMutatingAppointment}
					/>
				)}
			</>
		);
	}
);
CalendarContent.displayName = "CalendarContent";